import React from "react";
import { Paragraph } from "@singlestore/fusion/components/typography";
import { cx } from "class-variance-authority";

import { Override } from "types/utils";

import "./Input.scss";

type BaseInputProperties = {
    value: string;
    setValue: React.Dispatch<React.SetStateAction<string>>;
};

type TextAreaInputProps = BaseInputProperties &
    React.HTMLProps<HTMLTextAreaElement>;

function TextAreaInput({
    className,
    value,
    setValue,
    rows = 4,
    ...rest
}: TextAreaInputProps) {
    const classes = cx("input", "textarea", className);

    return (
        <textarea
            {...rest}
            rows={rows}
            className={classes}
            value={value}
            onChange={(event) => setValue(event.target.value)}
        />
    );
}

type RegularInputProps = BaseInputProperties &
    React.HTMLProps<HTMLInputElement>;

function RegularInput({
    className,
    value,
    setValue,
    ...rest
}: RegularInputProps) {
    const classes = cx("input", className);

    return (
        <input
            {...rest}
            className={classes}
            value={value}
            onChange={(event) => setValue(event.target.value)}
        />
    );
}

type InputType = React.HTMLProps<HTMLInputElement>["type"] | "textarea";

type CustomInputProps = {
    type?: InputType;
    label?: React.ReactNode | Array<React.ReactNode>;
    helperText?: string;
};

type InputProps = Override<
    TextAreaInputProps | RegularInputProps,
    CustomInputProps
>;

export function Input({
    type,
    id: _id,
    label: _label,
    helperText: _helperText,
    ...props
}: InputProps) {
    const reactId = React.useId();

    const id = _id || reactId;

    let label;

    if (_label) {
        label = (
            <label htmlFor={id} className="input-label">
                {_label}
            </label>
        );
    }

    let input = (
        <RegularInput type={type} id={id} {...(props as RegularInputProps)} />
    );

    if (type === "textarea") {
        input = <TextAreaInput id={id} {...(props as TextAreaInputProps)} />;
    }

    let helperText;

    if (_helperText) {
        helperText = (
            <Paragraph variant="annotation-1" mt="1x">
                {_helperText}
            </Paragraph>
        );
    }

    return (
        <div className="input-group">
            {label}
            {input}
            {helperText}
        </div>
    );
}
